import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';

interface UserSeedResult {
  tenant: { tenant_id: string; name: string };
  user: { user_id: string; first_name: string; last_name: string; email: string };
  created: boolean;
}

interface TenantCreateResult {
  tenant_id: string;
  name: string;
}

export default function Seed() {
  const qc = useQueryClient();

  // Create user form
  const [u_first, set_u_first] = useState('');
  const [u_last, set_u_last] = useState('');
  const [u_email, set_u_email] = useState('');
  const [u_company, set_u_company] = useState('');
  const [u_busy, set_u_busy] = useState(false);
  const [u_error, set_u_error] = useState<string | null>(null);
  const [u_result, set_u_result] = useState<UserSeedResult | null>(null);

  async function handle_seed_user(e: FormEvent) {
    e.preventDefault();
    set_u_busy(true);
    set_u_error(null);
    set_u_result(null);
    try {
      const result = await api.post<UserSeedResult>('/api/seed/user', {
        first_name: u_first.trim(),
        last_name: u_last.trim(),
        email: u_email.trim(),
        company: u_company.trim(),
      });
      set_u_result(result);
      qc.invalidateQueries({ queryKey: ['tenants'] });
      set_u_first('');
      set_u_last('');
      set_u_email('');
      set_u_company('');
    } catch (err) {
      set_u_error(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_u_busy(false);
    }
  }

  // Create tenant form
  const [t_name, set_t_name] = useState('');
  const [t_busy, set_t_busy] = useState(false);
  const [t_error, set_t_error] = useState<string | null>(null);
  const [t_result, set_t_result] = useState<TenantCreateResult | null>(null);

  async function handle_create_tenant(e: FormEvent) {
    e.preventDefault();
    set_t_busy(true);
    set_t_error(null);
    set_t_result(null);
    try {
      const result = await api.post<TenantCreateResult>('/api/seed/tenant', {
        name: t_name.trim(),
      });
      set_t_result(result);
      qc.invalidateQueries({ queryKey: ['tenants'] });
      set_t_name('');
    } catch (err) {
      set_t_error(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_t_busy(false);
    }
  }

  return (
    <div className="container" style={{ maxWidth: 720 }}>
      <h1>Seed</h1>
      <p className="muted" style={{ marginTop: -8, marginBottom: 24 }}>
        Pre-provision tenants and users in the platform Firestore.
      </p>

      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ marginTop: 0 }}>Create user</h2>
        <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
          Calls <code>find_or_create_user_by_email</code>. If a tenant with the
          same company name already has users from this email's domain, the new
          user joins that tenant. Otherwise a new tenant is created.
        </p>
        <form onSubmit={handle_seed_user} style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>First name</span>
              <input value={u_first} onChange={(e) => set_u_first(e.target.value)} required maxLength={80} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Last name</span>
              <input value={u_last} onChange={(e) => set_u_last(e.target.value)} required maxLength={80} />
            </label>
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Email</span>
            <input type="email" value={u_email} onChange={(e) => set_u_email(e.target.value)} required maxLength={254} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Company</span>
            <input value={u_company} onChange={(e) => set_u_company(e.target.value)} required maxLength={120} />
          </label>
          {u_error && <div className="error">{u_error}</div>}
          <div>
            <button type="submit" disabled={u_busy}>
              {u_busy ? 'Creating...' : 'Create user'}
            </button>
          </div>
        </form>
        {u_result && (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              background: 'var(--green-50)',
              borderRadius: 6,
              fontSize: 14,
            }}
          >
            <strong>{u_result.created ? 'Created' : 'Joined existing'}:</strong>{' '}
            <Link to={`/tenants/${u_result.tenant.tenant_id}/users/${u_result.user.user_id}`}>
              {u_result.user.first_name} {u_result.user.last_name}
            </Link>{' '}
            in tenant{' '}
            <Link to={`/tenants/${u_result.tenant.tenant_id}`}>
              {u_result.tenant.name}
            </Link>
          </div>
        )}
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Create empty tenant</h2>
        <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
          Use this only when you need a tenant with no initial user. Most
          seeding should go through "Create user" above.
        </p>
        <form onSubmit={handle_create_tenant} style={{ display: 'grid', gap: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Tenant name</span>
            <input value={t_name} onChange={(e) => set_t_name(e.target.value)} required maxLength={120} />
          </label>
          {t_error && <div className="error">{t_error}</div>}
          <div>
            <button type="submit" disabled={t_busy}>
              {t_busy ? 'Creating...' : 'Create tenant'}
            </button>
          </div>
        </form>
        {t_result && (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              background: 'var(--green-50)',
              borderRadius: 6,
              fontSize: 14,
            }}
          >
            <strong>Created:</strong>{' '}
            <Link to={`/tenants/${t_result.tenant_id}`}>{t_result.name}</Link>{' '}
            <code style={{ fontSize: 12 }}>({t_result.tenant_id})</code>
          </div>
        )}
      </div>
    </div>
  );
}
